import React, { useContext, useEffect, useRef } from 'react'
import { MenuItem } from '../models/MenuItem'
import { availableLang } from '../models/Lang';
import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StandardInput, StandardTextArea, } from '../styles/inputs';
import { InputContainer, MenuItemEditRowContainer } from '../styles/containers';
import { PrimarySwitch } from '../styles/switches';
import NumericInput from '../common/NumericInput';
import { ErrorIconButton, MenuIndexIconButton } from '../styles/buttons';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { errorColor, primary } from '../styles/colors';
import { SortableList } from '../sortable/SortableList';
import MenuItemEditRowHandle from './MenuItemEditRowHandle';
import { MenuItemIndexImage, MenuItemIndexImageContainer } from '../styles/images';
import useFetch from '../hooks/useFetch';
import { postUploadItemImage } from '../config/apiRoutes';
import UserContext from '../contexts/UserContext';
import AllergenAddMenuButton from './AllergenAddMenuButton';
import { Allergen } from '../models/Allergens';
import AllergenSelector from './AllergenSelector';
import useDebouncedValue from '../hooks/useDebouncedValue';

type Props = {
    menuItem: MenuItem;
    setMenuItem: (menuItem: MenuItem) => void;
    selectedLang: availableLang;
    onRemoveClick: () => void;
    isReorderAllowed?: boolean;
}

const maxFileSizeMb = 15;

const MenuItemEditRow = ({ menuItem, selectedLang, setMenuItem, onRemoveClick, isReorderAllowed }: Props) => {
    const { t } = useTranslation();
    const { selectedRestaurant } = useContext(UserContext);
    const uploader = useFetch(false, true);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [editedTitle, setEditedTitle] = React.useState<string>(getTitleValue());
    useDebouncedValue(editedTitle, 300, updateTitleValue);
    const [editedDescription, setEditedDescription] = React.useState<string>(getDescriptionValue());
    useDebouncedValue(editedDescription, 300, updateDescriptionChange);
    const [editedPrice, setEditedPrice] = React.useState<number>(menuItem.priceGross);
    useDebouncedValue(editedPrice, 300, handlePriceChange);
    const [editedAllergens, setEditedAllergens] = React.useState<Allergen[]>(menuItem.allergens);
    useDebouncedValue(editedAllergens, 300, handleAllergenChange);
    const [editedImage, setEditedImage] = React.useState<string>(menuItem.imageUrl || "../assets/logo_white.svg");
    const [confirmDelete, setConfirmDelete] = React.useState(false);

    useEffect(() => {
        setEditedTitle(getTitleValue());
        setEditedDescription(getDescriptionValue());
        setEditedPrice(menuItem.priceGross);
    }, [selectedLang]);

    function getTitleValue() {
        const relevantTranslation = menuItem.translations.find(translation => translation.isoCode.toLowerCase() === selectedLang);
        return relevantTranslation ? relevantTranslation.title : "";
    }

    function getDescriptionValue() {
        const relevantTranslation = menuItem.translations.find(translation => translation.isoCode.toLowerCase() === selectedLang);
        return relevantTranslation ? relevantTranslation.description : "";
    }

    function updateTitleValue(newValue: string) {
        const relevantTranslation = menuItem.translations.find(translation => translation.isoCode.toLowerCase() === selectedLang);
        if (!relevantTranslation) return;
        const newTranslation = {
            ...relevantTranslation,
            title: newValue
        }
        if (relevantTranslation) {
            setMenuItem({
                ...menuItem,
                translations: menuItem.translations.map(translation => translation.id === relevantTranslation.id ? newTranslation : translation)
            });
        }
    }

    function updateDescriptionChange(newValue: string) {
        const relevantTranslation = menuItem.translations.find(translation => translation.isoCode.toLowerCase() === selectedLang);
        if (!relevantTranslation) return;
        const newTranslation = {
            ...relevantTranslation,
            description: newValue
        }
        if (relevantTranslation) {
            setMenuItem({
                ...menuItem,
                translations: menuItem.translations.map(translation => translation.id === relevantTranslation.id ? newTranslation : translation)
            });
        }
    }

    function handlePriceChange(newPrice: number) {
        setMenuItem({
            ...menuItem,
            priceGross: newPrice
        });
    }

    function handleIsActiveCHange(e: React.ChangeEvent<HTMLInputElement>) {
        setMenuItem({
            ...menuItem,
            isActive: e.target.checked
        });
    }

    function handleFileUploadClick() {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) return;
        if (selectedRestaurant == null) return;
        const sizeInBytes = e.target.files[0].size / 1024 / 1024;
        if (sizeInBytes > maxFileSizeMb) {
            setErrorMessage(t("gallery.maximum_file_size"));
            return false;
        }
        const formData = new FormData();
        formData.append("file", e.target.files[0]);

        const response = await uploader.call<any, any>("POST", postUploadItemImage.replace(":restaurantShortName", selectedRestaurant?.restaurantShortName), formData);
        if (response.ok && response.data) {
            setMenuItem({
                ...menuItem,
                imageUrl: response.data.originalUrl
            });
            setEditedImage(response.data.originalUrl);
            setErrorMessage(null);
            return true;
        } else {
            return false;
        }
    }

    function handleAllergenChange() {
        setMenuItem({ ...menuItem, allergens: editedAllergens });
    }

    return (
        <>
            <SortableList.Item id={menuItem.id}>
                <MenuItemEditRowContainer pt={2.5} pb={2} px={1}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4} md={1} lg={1} >
                            <Typography fontSize={"12px"} fontWeight={600} mb={0.5}>
                                {t("menu.index_image")}
                            </Typography>
                            <MenuItemIndexImageContainer>
                                <MenuItemIndexImage
                                    src={editedImage}
                                    style={{
                                        transform: editedImage ? "scale(1)" : "scale(0.7)",
                                    }}
                                />
                                <MenuIndexIconButton
                                    onClick={handleFileUploadClick}
                                    disabled={uploader.loading}
                                >
                                    {uploader.loading && (
                                        <CircularProgress
                                            size="16px"
                                            style={{ color: primary.light }}
                                        />
                                    )}
                                    {!uploader.loading && (
                                        <img
                                            src="../assets/open-img.svg"
                                            width="16px"
                                            height="16px"
                                        />
                                    )}
                                </MenuIndexIconButton>
                            </MenuItemIndexImageContainer>
                            <input
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                type="file"
                                accept="image/*"
                                onChange={handleFileUpload}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2} lg={2} >
                            <Typography fontSize={"12px"} fontWeight={600} mb={0.5}>
                                {t("common.name")}:
                            </Typography>
                            <StandardInput
                                value={editedTitle}
                                onChange={(e: any) => setEditedTitle(e.target.value)}
                                placeholder={t("menu.no_item_name")}
                                disabled={uploader.loading}
                            />
                            <Typography fontSize={"12px"} fontWeight={600} pt={1} pb={0.5}>
                                {t("common.status")}:
                            </Typography>
                            <InputContainer>
                                <div className="flex items-center justify-between w-full h-full px-2">
                                    <div className="text-[14px] flex items-center">
                                        {menuItem.isActive ? t("common.available") : t("common.unavailable")}
                                    </div>
                                    <PrimarySwitch size="small"
                                        checked={menuItem.isActive}
                                        onChange={handleIsActiveCHange}
                                    />
                                </div>
                            </InputContainer>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Typography fontSize={"12px"} fontWeight={600} mb={0.5}>
                                {t("common.description")}:
                            </Typography>
                            <Box>
                                <StandardTextArea
                                    value={editedDescription}
                                    onChange={(e: any) => setEditedDescription(e.target.value)}
                                    style={{ height: "100px" }}
                                    disabled={uploader.loading}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Grid container spacing={1}>
                                <Grid item xs>
                                    <Typography fontSize={"12px"} fontWeight={600} mb={0.5}>
                                        {t("menu.allergens")}:
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Box textAlign={"right"} style={{ marginTop: "-10px" }}>
                                        <AllergenAddMenuButton
                                            allergens={editedAllergens}
                                            setAllergens={setEditedAllergens}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <AllergenSelector
                                allergens={editedAllergens}
                                setAllergens={setEditedAllergens}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2} lg={2} >
                            <Typography fontSize={"12px"} fontWeight={600} pb={0.5}>
                                {t("common.price")}:
                            </Typography>
                            <Box className={uploader.loading ? "pointer-events-none opacity-50" : ""}>
                                <NumericInput
                                    value={editedPrice}
                                    unitOfMeasure='Ft'
                                    setValue={setEditedPrice}
                                />
                            </Box>
                            <Box mt={3} display="flex" justifyContent="flex-end" alignItems="center">
                                {!confirmDelete && (
                                    <ErrorIconButton onClick={() => setConfirmDelete(true)}>
                                        <DeleteForeverIcon style={{ color: errorColor }} />
                                    </ErrorIconButton>
                                )}
                                {confirmDelete && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            className="text-xs px-2 py-1 rounded border border-neutral text-utility-mutedText hover:bg-neutral"
                                            onClick={() => setConfirmDelete(false)}
                                        >
                                            {t("common.cancel")}
                                        </button>
                                        <button
                                            className="text-xs px-2 py-1 rounded bg-error text-white hover:opacity-90"
                                            onClick={onRemoveClick}
                                        >
                                            {t("common.delete")}
                                        </button>
                                    </div>
                                )}
                            </Box>
                        </Grid>
                        {isReorderAllowed && (
                            <Grid item style={{ width: "50px" }}>
                                <MenuItemEditRowHandle />
                            </Grid>
                        )}

                    </Grid>
                    {errorMessage && (
                        <Alert severity="error">{errorMessage}</Alert>
                    )}
                    {uploader.error && (
                        <Alert severity="error">{uploader.errorMessage}</Alert>
                    )}
                </MenuItemEditRowContainer>
            </SortableList.Item>
        </>
    )
}

export default React.memo(MenuItemEditRow);